import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  CameraIcon,
  Check,
  Facebook,
  Heart,
  Instagram,
  Link2,
  MessageCircle,
  ShieldCheck,
  Star,
  Upload,
  X,
} from "lucide-react";
import { getCategory, buildCategory, CATEGORY_META } from "../data/experience";
import { BRAND } from "../data/content";
import {
  addComment,
  addUserPhoto,
  formatEntryDate,
  formatRelativeTime,
  getComments,
  getLikes,
  getUserPhotos,
  initials,
  subscribe,
  toggleLike,
} from "../lib/social";
import Header from "./Header";
import Footer from "./Footer";
import StickyMobileCTA from "./StickyMobileCTA";
import BackToTop from "./BackToTop";
import { Reveal } from "./Reveal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const ACCENT_CLASSES = {
  teal: { dot: "bg-brand-teal", text: "text-brand-teal", chip: "bg-brand-teal/15 text-brand-teal" },
  gold: { dot: "bg-brand-gold", text: "text-brand-gold", chip: "bg-brand-gold/15 text-brand-gold" },
  green: { dot: "bg-brand-green", text: "text-brand-green", chip: "bg-brand-green/15 text-brand-green" },
};

// ---------- Star rating control ------------------------------------------------
const StarRating = ({ value, onChange, size = 18, readOnly = false }) => {
  return (
    <div
      className="inline-flex items-center gap-0.5"
      data-testid="star-rating"
      role={readOnly ? "img" : "radiogroup"}
      aria-label={readOnly ? `${value} out of 5 stars` : "Choose a rating"}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        return readOnly ? (
          <Star
            key={n}
            className={filled ? "text-brand-gold fill-brand-gold" : "text-brand-charcoal/20"}
            style={{ width: size, height: size }}
            aria-hidden="true"
          />
        ) : (
          <button
            key={n}
            type="button"
            aria-label={`${n} ${n === 1 ? "star" : "stars"}`}
            data-testid={`star-${n}`}
            onClick={() => onChange(n)}
            className="p-0.5 rounded transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal"
          >
            <Star
              className={filled ? "text-brand-gold fill-brand-gold" : "text-brand-charcoal/30"}
              style={{ width: size, height: size }}
            />
          </button>
        );
      })}
    </div>
  );
};

// ---------- Avatar -------------------------------------------------------------
const Avatar = ({ name, className = "" }) => (
  <span
    className={`inline-flex items-center justify-center h-9 w-9 rounded-full bg-brand-charcoal text-brand-gold text-[12px] font-bold tracking-wider ${className}`}
    aria-hidden="true"
  >
    {initials(name)}
  </span>
);

// ---------- Share row ----------------------------------------------------------
const ShareRow = ({ entry, categoryTitle }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}#${entry.id}`
      : "";
  const shareText = `${entry.title} — ${categoryTitle} · Fresh Fast Foods LLC`;

  const onInstagram = () => {
    // Instagram has no public web-share intent for posts; open profile so the
    // user can post manually. Copying the share text to clipboard helps.
    navigator.clipboard?.writeText(`${shareText} ${shareUrl}`).catch(() => {});
    window.open(BRAND.social.instagram, "_blank", "noopener,noreferrer");
  };
  const onFacebook = () => {
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-row">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/55 mr-1">
        Share
      </span>
      <button
        type="button"
        onClick={onInstagram}
        data-testid="share-instagram"
        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-brand-charcoal hover:text-white hover:border-brand-charcoal transition-colors"
      >
        <Instagram className="h-3.5 w-3.5" />
        Instagram
      </button>
      <button
        type="button"
        onClick={onFacebook}
        data-testid="share-facebook"
        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-brand-charcoal hover:text-white hover:border-brand-charcoal transition-colors"
      >
        <Facebook className="h-3.5 w-3.5" />
        Facebook
      </button>
      <button
        type="button"
        onClick={onCopy}
        data-testid="share-copy"
        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-brand-teal hover:text-white hover:border-brand-teal transition-colors"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
};

// ---------- Comment form -------------------------------------------------------
const CommentForm = ({ entryId, onPosted }) => {
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [posted, setPosted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Please enter your name.");
    if (!text.trim()) return setError("Please enter your comment.");
    addComment(entryId, { name, rating, text });
    setName("");
    setText("");
    setRating(5);
    setPosted(true);
    if (onPosted) onPosted();
    setTimeout(() => setPosted(false), 3500);
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="comment-form"
      className="mt-4 rounded-2xl bg-white border border-black/5 p-4 sm:p-5"
    >
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
            Your name
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="comment-name"
            placeholder="e.g. Sam from Brooklyn"
            className="mt-1.5 h-10 rounded-xl bg-brand-cream/40 border-black/10 text-[14px]"
          />
        </div>
        <div className="flex flex-col">
          <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
            Your rating
          </Label>
          <div className="mt-1.5 h-10 inline-flex items-center">
            <StarRating value={rating} onChange={setRating} size={22} />
          </div>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
          Your comment
        </Label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="comment-text"
          placeholder="Share your thoughts about this event…"
          rows={3}
          maxLength={600}
          className="mt-1.5 rounded-xl bg-brand-cream/40 border-black/10 text-[14px]"
        />
        <p className="mt-1 text-[10.5px] text-brand-charcoal/40 text-right">{text.length}/600</p>
      </div>
      {error ? (
        <p data-testid="comment-error" role="alert" className="text-[12px] text-red-600 mt-2">
          {error}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="inline-flex items-center gap-1.5 text-[11px] text-brand-charcoal/55">
          <ShieldCheck className="h-3.5 w-3.5 text-brand-teal" />
          Comments are subject to moderation before they appear publicly.
        </p>
        <button
          type="submit"
          data-testid="comment-submit"
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-brand-teal transition-colors"
        >
          {posted ? "Posted · Awaiting review" : "Post Comment"}
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </form>
  );
};

// ---------- Photo upload (mocked) ----------------------------------------------
const PhotoUpload = ({ entryId, onPosted }) => {
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [posted, setPosted] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Please choose an image under 5 MB.");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!preview) return setError("Please choose a photo.");
    if (!name.trim()) return setError("Please add your name.");
    addUserPhoto(entryId, { name, dataUrl: preview });
    setPreview(null);
    setName("");
    if (fileRef.current) fileRef.current.value = "";
    setPosted(true);
    if (onPosted) onPosted();
    setTimeout(() => setPosted(false), 3500);
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="photo-upload-form"
      className="mt-4 rounded-2xl bg-brand-charcoal text-white p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-brand-gold">
          <CameraIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="font-display font-bold text-[16px] tracking-tight">Post Your Photo</p>
          <p className="text-[12.5px] text-white/65 leading-snug">
            Were you at this event? Upload a photo to the Community Gallery — pending review.
          </p>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-[160px_1fr] gap-3 items-start">
        <label
          className={`relative flex flex-col items-center justify-center gap-1.5 h-[120px] rounded-2xl border-2 border-dashed transition-colors cursor-pointer ${
            preview ? "border-brand-teal/60 bg-white/[0.04]" : "border-white/20 hover:border-brand-teal/60 bg-white/[0.04]"
          }`}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            data-testid="photo-file-input"
            className="absolute inset-0 opacity-0 cursor-pointer"
            aria-label="Choose a photo"
          />
          {preview ? (
            <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover rounded-2xl" />
          ) : (
            <>
              <Upload className="h-5 w-5 text-white/60" />
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
                Choose photo
              </span>
              <span className="text-[10px] text-white/40">JPG / PNG · &lt; 5 MB</span>
            </>
          )}
        </label>
        <div className="flex flex-col gap-3">
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/55">
              Your name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="photo-name"
              placeholder="e.g. Maria L."
              className="mt-1.5 h-10 rounded-xl bg-white/[0.06] border-white/15 text-white text-[14px] placeholder:text-white/40"
            />
          </div>
          {error ? (
            <p data-testid="photo-error" role="alert" className="text-[12px] text-red-300">
              {error}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="inline-flex items-center gap-1.5 text-[11px] text-white/55">
              <ShieldCheck className="h-3.5 w-3.5 text-brand-gold" />
              Uploads are reviewed before appearing in the Community Gallery.
            </p>
            <button
              type="submit"
              data-testid="photo-submit"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-charcoal px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-brand-gold-dark transition-colors"
            >
              {posted ? "Uploaded · Awaiting review" : "Post to Community"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

// ---------- Single entry card --------------------------------------------------
const EntryCard = ({ entry, categoryTitle, accent }) => {
  const [tick, force] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    const unsub = subscribe(() => force((x) => x + 1));
    return unsub;
  }, []);

  const { count: likeCount, liked } = getLikes(entry.id);
  // Re-read directly each render so comments/photos refresh when `subscribe()` fires.
  // (`tick` ensures the linter knows we depend on the social store.)
  // eslint-disable-next-line no-unused-vars
  const _socialTick = tick;
  const comments = getComments(entry.id);
  const userPhotos = getUserPhotos(entry.id);

  return (
    <article
      id={entry.id}
      data-testid={`entry-card-${entry.id}`}
      className="rounded-3xl bg-white border border-black/5 shadow-[0_8px_30px_-15px_rgba(17,17,17,0.18)] overflow-hidden"
    >
      {/* Header — date, title */}
      <div className="px-5 sm:px-7 pt-6 sm:pt-7 pb-4">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/55">
          <Calendar className="h-3.5 w-3.5" />
          <time dateTime={entry.date}>{formatEntryDate(entry.date)}</time>
          <span className={`ml-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 ${accent.chip}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
            {categoryTitle}
          </span>
        </div>
        <h2 className="mt-3 font-display font-black text-2xl sm:text-[28px] text-brand-charcoal tracking-tight leading-[1.1]">
          {entry.title}
        </h2>
        <p className="mt-2 text-[14.5px] text-brand-charcoal/75 leading-relaxed">{entry.description}</p>
      </div>

      {/* Images */}
      <div className={`px-5 sm:px-7 pb-5 ${entry.images.length > 1 ? "grid grid-cols-2 gap-3" : ""}`}>
        {entry.images.map((src, i) => (
          <div
            key={src + i}
            className={`relative overflow-hidden rounded-2xl ${entry.images.length === 1 ? "aspect-[16/10]" : "aspect-square"}`}
          >
            <img
              src={src}
              alt={`${entry.title} — photo ${i + 1}`}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Tags */}
      {entry.tags?.length ? (
        <div className="px-5 sm:px-7 pb-4 flex flex-wrap gap-1.5">
          {entry.tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-full bg-brand-cream/80 border border-black/5 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/70"
            >
              #{t}
            </span>
          ))}
        </div>
      ) : null}

      {/* Action bar */}
      <div className="px-5 sm:px-7 py-3 border-t border-black/5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => toggleLike(entry.id)}
            data-testid={`like-btn-${entry.id}`}
            aria-pressed={liked}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-bold transition-colors ${
              liked
                ? "bg-red-50 border-red-200 text-red-600"
                : "bg-white border-black/10 text-brand-charcoal hover:border-red-200 hover:text-red-600"
            }`}
          >
            <Heart className={`h-3.5 w-3.5 ${liked ? "fill-red-500 text-red-500" : ""}`} />
            <span>{likeCount > 0 ? likeCount : "Like"}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowComments((v) => !v)}
            data-testid={`comment-toggle-${entry.id}`}
            aria-expanded={showComments}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1.5 text-[12px] font-bold text-brand-charcoal hover:border-brand-teal/40 hover:text-brand-teal transition-colors"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            {comments.length > 0 ? `${comments.length} comment${comments.length === 1 ? "" : "s"}` : "Comment"}
          </button>
          <button
            type="button"
            onClick={() => setShowUpload((v) => !v)}
            data-testid={`upload-toggle-${entry.id}`}
            aria-expanded={showUpload}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-3 py-1.5 text-[12px] font-bold text-brand-charcoal hover:border-brand-teal/40 hover:text-brand-teal transition-colors"
          >
            <CameraIcon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Post your photo</span>
            <span className="sm:hidden">Photo</span>
          </button>
        </div>
        <ShareRow entry={entry} categoryTitle={categoryTitle} />
      </div>

      {/* Comment + Upload area */}
      {(showComments || showUpload) ? (
        <div className="px-5 sm:px-7 py-5 bg-brand-cream/50 border-t border-black/5">
          {showUpload ? <PhotoUpload entryId={entry.id} /> : null}

          {showComments ? <CommentForm entryId={entry.id} /> : null}

          {showComments && comments.length > 0 ? (
            <ul className="mt-5 space-y-4" data-testid={`comments-list-${entry.id}`}>
              {comments.map((c) => (
                <li
                  key={c.id}
                  data-testid={`comment-${c.id}`}
                  className="flex items-start gap-3 rounded-2xl bg-white border border-black/5 p-4"
                >
                  <Avatar name={c.name} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                      <p className="font-display font-bold text-[14px] text-brand-charcoal tracking-tight">
                        {c.name}
                      </p>
                      <StarRating value={c.rating} readOnly size={14} />
                      <span className="text-[11px] text-brand-charcoal/45">
                        · {formatRelativeTime(c.createdAt)}
                      </span>
                      {c.pending ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/20 text-brand-gold-dark px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                          <ShieldCheck className="h-3 w-3" /> Pending review
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 text-[13.5px] text-brand-charcoal/85 leading-snug whitespace-pre-wrap">
                      {c.text}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          {showUpload && userPhotos.length > 0 ? (
            <div className="mt-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/55">
                Community Gallery — {userPhotos.length} photo{userPhotos.length === 1 ? "" : "s"}
              </p>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {userPhotos.map((p) => (
                  <figure
                    key={p.id}
                    data-testid={`user-photo-${p.id}`}
                    className="relative aspect-square overflow-hidden rounded-xl border border-black/5"
                  >
                    <img src={p.dataUrl} alt={`Posted by ${p.name}`} className="absolute inset-0 h-full w-full object-cover" />
                    <figcaption className="absolute bottom-0 inset-x-0 px-2 py-1 bg-gradient-to-t from-brand-charcoal/85 to-transparent text-white text-[10px] font-semibold truncate">
                      {p.name}
                    </figcaption>
                    {p.pending ? (
                      <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-brand-gold text-brand-charcoal px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                        <ShieldCheck className="h-2.5 w-2.5" />
                      </span>
                    ) : null}
                  </figure>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

// ---------- Main page ----------------------------------------------------------
export const ExperiencePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [apiCategory, setApiCategory] = useState(null);
  const [loadedSlug, setLoadedSlug] = useState(null);

  // Hydrate from /api/experiences so admin edits are reflected live.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${apiBase}/api/experiences?category=${encodeURIComponent(slug || "")}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!mounted) return;
        setApiCategory(buildCategory(slug, data.items || []));
        setLoadedSlug(slug);
      } catch {
        if (mounted) {
          setApiCategory(null);
          setLoadedSlug(slug);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  // Prefer API-hydrated category when it has entries; fall back to static seed.
  const staticCategory = getCategory(slug);
  const category =
    apiCategory && apiCategory.entries && apiCategory.entries.length > 0
      ? apiCategory
      : staticCategory;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!category) {
    return (
      <div className="App font-sans bg-white text-brand-charcoal antialiased">
        <Header />
        <main className="pt-24 sm:pt-28 lg:pt-36 pb-24 max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 text-center">
          <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-teal">
            Not Found
          </p>
          <h1 className="mt-3 font-display font-black text-3xl sm:text-4xl text-brand-charcoal tracking-tight">
            We couldn&rsquo;t find that experience.
          </h1>
          <p className="mt-3 text-[15px] text-brand-charcoal/65 leading-relaxed">
            The category you&rsquo;re looking for doesn&rsquo;t exist. Pick a topic to explore:
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(CATEGORY_META).map(([s, m]) => (
              <Link
                key={s}
                to={`/experience/${s}`}
                className="rounded-2xl bg-brand-cream border border-black/5 px-4 py-3 text-[13px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-white hover:border-brand-teal/30 transition-colors"
              >
                {m.title}
              </Link>
            ))}
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.14em] hover:bg-brand-teal transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Main Site
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const accent = ACCENT_CLASSES[category.accent] || ACCENT_CLASSES.teal;

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-24 sm:pt-28 lg:pt-32 pb-10 sm:pb-12 bg-brand-charcoal grain overflow-x-hidden">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <Link
                to="/#trust-strip"
                data-testid="back-to-main-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Experience
              </Link>
            </Reveal>
            <Reveal delay={1}>
              <p className={`mt-6 text-[12px] font-bold uppercase tracking-[0.22em] ${accent.text}`}>
                {category.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={2}>
              <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]">
                {category.title}
                <span className="text-brand-gold">.</span>
              </h1>
            </Reveal>
            <Reveal delay={3}>
              <p className="mt-4 max-w-2xl text-[15px] lg:text-[17px] text-white/70 leading-relaxed">
                {category.description}
              </p>
            </Reveal>
            <Reveal delay={4}>
              <p className="mt-6 text-[12px] uppercase tracking-[0.22em] text-white/45 font-bold">
                {category.entries.length} {category.entries.length === 1 ? "Update" : "Updates"}
              </p>
            </Reveal>
          </div>
        </section>

        {/* Other categories nav */}
        <div className="bg-white border-y border-black/5">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 py-4 flex gap-2 overflow-x-auto no-scrollbar">
            {Object.entries(CATEGORY_META).map(([s, m]) => (
              <Link
                key={s}
                to={`/experience/${s}`}
                data-testid={`category-pill-${s}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                  s === slug
                    ? "bg-brand-charcoal text-white"
                    : "bg-brand-cream/60 text-brand-charcoal/70 hover:bg-brand-cream"
                }`}
              >
                {m.title}
              </Link>
            ))}
          </div>
        </div>

        {/* Entries */}
        <section className="py-14 sm:py-16 bg-brand-gray">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 space-y-6 sm:space-y-7">
            {category.entries.length === 0 ? (
              <Reveal>
                <div className="rounded-3xl bg-white border border-black/5 px-6 py-12 text-center">
                  <p className="font-display font-bold text-xl text-brand-charcoal">
                    Updates coming soon.
                  </p>
                  <p className="mt-2 text-[14px] text-brand-charcoal/60">
                    We&rsquo;re putting together updates for this category. Check back shortly.
                  </p>
                </div>
              </Reveal>
            ) : (
              category.entries.map((entry, i) => (
                <Reveal key={entry.id} delay={Math.min(i + 1, 6)}>
                  <EntryCard entry={entry} categoryTitle={category.title} accent={accent} />
                </Reveal>
              ))
            )}
          </div>
        </section>
      </main>
      <Footer />
      <StickyMobileCTA />
      <BackToTop />
    </div>
  );
};

export default ExperiencePage;
